import { apiGet, apiPost, logApiError } from "./api";
import type { SessionData } from "./data/sessionData.ts";

/* GET a backend endpoint and return its payload, or undefined when the call failed. */
async function getJson(url: string) {
	try {
		return (await apiGet(url)).data;
	} catch (error) {
		logApiError(error);
	}
}

export async function fetchState(hash: string = '', clubId: string | null = null): Promise<SessionData> {
	let url = '/resa/state';
	if (hash) {
		url = url + '/' + hash;
	}
	if (clubId) {
		url = url + '?clubId=' + clubId;
	}

	return getJson(url);
}

export async function fetchOffer(hash: string) {
	return getJson('/resa/offer/' + hash);
}

export async function fetchContact(hash: string) {
	const contact = await getJson('/resa/contact/' + hash);

	if (!contact) {
		return contact;
	}

	// A contact starts life as a bare temp record — clubId and nothing else —
	// so address is null until the details form is submitted. Flattening it
	// without a guard throws and takes the whole form down.
	return {
		...contact,
		addressLocality: contact.address?.addressLocality,
		addressCountryIso: contact.address?.addressCountryIso,
		streetAddress: contact.address?.streetAddress,
		postalCode: contact.address?.postalCode,
	};
}

export async function fetchCountries(hash: string) {
	return getJson('/resa/countries/' + hash);
}

export async function fetchSale(hash: string) {
	return getJson('/resa/sale/' + hash);
}

export async function fetchPayable(hash: string) {
	return getJson('/resa/payable/' + hash);
}

// SPIKE — discount codes.
// Codes the club has made available for this sale (may be empty). For discovering /
// surfacing a campaign the customer is eligible for.
export async function fetchDiscountCodes(hash: string) {
	return getJson('/resa/discountCodes/' + hash);
}

// Apply a discount code; the server re-issues the payable so the reduced total flows
// through. Returns the promise so the caller can show success/error and reload details.
export function applyDiscount(hash: string, code: string, setState: Function) {
	return apiPost('/resa/applyDiscount/' + hash, { code }).then((response) => {
		setState(response.data);
	});
}

// The automatic goodwill-gesture discount already applied to this sale (auto-applied
// when the sale was created), as { name, description } — or null when there is none.
// For the "you're getting a discount" banner.
export async function fetchDiscount(hash: string) {
	return getJson('/resa/discount/' + hash);
}

// Catalogue-level: the discounts the club is currently running, for badging the plan
// cards and early steps before a sale exists. Each: { name, description, shortLabel,
// appliesToSubscription, appliesToOneOff }. [] when none.
export async function fetchAvailableDiscounts(hash: string) {
	return getJson('/resa/discounts/' + hash);
}

// Re-issue the payable so the payment widget mounts against a fresh, unexpired token.
// Called whenever the payment screen loads (forward, back, or refresh); the server
// sends the customer to the confirmation instead if the payment has already gone
// through, so it never risks a second charge. Returns the promise so the caller can
// wait before rendering the widget.
export function reinitPayment(hash: string, setState: Function) {
	return apiGet('/resa/reinitPayment/' + hash).then((response) => {
		setState(response.data);
	});
}

export function stepBack(hash: string, setState: Function) {
	apiGet('/resa/stepBack/' + hash).then((response) => {
		setState(response.data);
	}).catch(function (error) {
		logApiError(error);
		globalThis.location.reload();
	});
}

// Jump from a committed checkout to the (read-only) details form, and back.
export function reviewDetails(hash: string, setState: Function) {
	apiGet('/resa/reviewDetails/' + hash).then((response) => {
		setState(response.data);
	}).catch(function (error) {
		logApiError(error);
		globalThis.location.reload();
	});
}

export function resumeCheckout(hash: string, setState: Function) {
	apiGet('/resa/resumeCheckout/' + hash).then((response) => {
		setState(response.data);
	}).catch(function (error) {
		logApiError(error);
		globalThis.location.reload();
	});
}

// Drop the stored session and start a completely fresh checkout. The humane
// replacement for "clear your browser storage by hand" once a checkout is
// committed and cannot be edited in place.
export function restartCheckout() {
	try {
		localStorage.removeItem('tokenHash');
	} catch (error) {
		// Storage unavailable (private mode, blocked). The reload still drops the
		// in-memory session and starts a fresh one, which is the point.
	}
	globalThis.location.reload();
}

// Re-issue the SMS signature so the customer gets a fresh code. Returns the promise
// so the caller can show "sent" / error feedback; the step is unchanged, so the
// verification dialog stays open.
export function resendCode(hash: string, setState: Function) {
	return apiGet('/resa/resendCode/' + hash).then((response) => {
		setState(response.data);
	});
}

// Step-indicator navigation: jump back to an earlier, already-passed step. The
// server only honours the two review steps (start date = 2, details = 3) and only
// backwards, so an out-of-range value is a no-op.
export function goToStep(hash: string, step: number, setState: Function) {
	apiGet('/resa/goToStep/' + step + '/' + hash).then((response) => {
		setState(response.data);
	}).catch(function (error) {
		logApiError(error);
		globalThis.location.reload();
	});
}

/*
	The widget telling us the payment succeeded is not proof — the server checks
	with Resamania before it will mark a session paid, so this endpoint can come
	back "not settled yet" rather than success or failure.

	That happens when the PSP has taken the card but Resamania has not yet
	reconciled. Retrying briefly covers it. If it still has not settled after a
	few seconds we stop and leave the customer where they are, rather than
	claiming a success or a failure we cannot evidence.
 */
const PAYMENT_SETTLE_ATTEMPTS = 5;
const PAYMENT_SETTLE_DELAY_MS = 1500;

export async function gotoMessagePaymentSuccess(hash: string, setState: Function) {
	for (let attempt = 1; attempt <= PAYMENT_SETTLE_ATTEMPTS; attempt++) {
		try {
			const response = await apiGet('/resa/message/success/' + hash);
			const session = response.data;

			// paid, or the server has already moved us to the failure step
			// step 9 = declined, step 8 = charged but unconfirmed. Both are answers.
			const settled = session?.paid === true || session?.step === 9 || session?.step === 8;

			if (settled || attempt === PAYMENT_SETTLE_ATTEMPTS) {
				setState(session);
				return;
			}
		} catch (error: any) {
			logApiError(error);
			if (attempt === PAYMENT_SETTLE_ATTEMPTS) {
				return;
			}
		}

		await new Promise((resolve) => setTimeout(resolve, PAYMENT_SETTLE_DELAY_MS));
	}
}

export function gotoMessagePaymentFailure(hash: string, setState: Function) {
	apiGet('/resa/message/failure/' + hash).then((response) => {
		setState(response.data);
	}).catch(logApiError);
}
